import { useState, useEffect, useCallback } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import logger from '@/lib/logger';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Search, LogOut, LayoutDashboard, Users, Briefcase, 
  Building2, GitBranch, Link2, Calendar, FileText, BookOpen, 
  History as HistoryIcon, Lock, Unlock, GripVertical, FileCheck, FolderOpen, Menu, X,
  MessageSquare, RotateCcw as RotateCcwIcon, BarChart3, ChevronDown, ChevronRight,
  ShieldCheck
} from 'lucide-react';
import Onboarding from '@/components/Onboarding';

// Default navigation items — grouped for visual streamlining.
// `group: null` means the item lives in the top-level (un-grouped) section.
const DEFAULT_NAV_ITEMS = [
  { path: 'overview', label: 'Overview', icon: 'LayoutDashboard', group: null },
  { path: 'portfolio', label: 'Portfolio', icon: 'BarChart3', group: null },
  { path: 'individuals', label: 'Individuals', icon: 'Users', group: 'people' },
  { path: 'advisers', label: 'Advisers', icon: 'Briefcase', group: 'people' },
  { path: 'entities', label: 'Entities & Roles', icon: 'Building2', group: 'people' },
  { path: 'reviews', label: 'Recurring Reviews', icon: 'RotateCcwIcon', group: 'people' },
  { path: 'documents', label: 'Documents', icon: 'FolderOpen', group: 'records' },
  { path: 'knowledge', label: 'Knowledge', icon: 'BookOpen', group: 'records' },
  { path: 'workflow', label: 'Workflow', icon: 'Calendar', group: 'operations' },
  { path: 'portal-access', label: 'Portal Access', icon: 'ShieldCheck', group: 'operations' },
  { path: 'history', label: 'History', icon: 'HistoryIcon', group: 'operations' },
];

// Group definitions — order matters; null group sections render in-place between groups.
const GROUPS = [
  { key: 'people', label: 'People & Roles' },
  { key: 'records', label: 'Records' },
  { key: 'operations', label: 'Operations' },
];

// Icon mapping
const ICON_MAP = {
  LayoutDashboard, Users, Briefcase, Building2, GitBranch, 
  Link2, Calendar, FileText, BookOpen, HistoryIcon, FileCheck, FolderOpen,
  MessageSquare, RotateCcwIcon, BarChart3, ShieldCheck,
};

export default function ClientLayout() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, loading: authLoading, logout } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Drag and drop state
  const [navItems, setNavItems] = useState(DEFAULT_NAV_ITEMS);
  const [isLocked, setIsLocked] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Collapsed groups (persisted per user)
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Redirect legacy /activity and /audit URLs to the merged /history page.
  useEffect(() => {
    const tail = location.pathname.split('/').pop();
    if (tail === 'activity') {
      navigate(`/app/clients/${clientId}/history`, { replace: true });
    } else if (tail === 'audit') {
      navigate(`/app/clients/${clientId}/history?view=audit`, { replace: true });
    }
  }, [location.pathname, clientId, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    const saved = localStorage.getItem(`qindex_nav_collapsed_${user.id}`);
    if (saved) {
      try { setCollapsedGroups(JSON.parse(saved)); } catch (e) { /* ignore */ }
    }
  }, [user?.id]);

  const toggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [groupKey]: !prev[groupKey] };
      if (user?.id) {
        localStorage.setItem(`qindex_nav_collapsed_${user.id}`, JSON.stringify(next));
      }
      return next;
    });
  };

  // Load saved nav order from localStorage
  useEffect(() => {
    if (user?.id) {
      const savedOrder = localStorage.getItem(`qindex_nav_order_${user.id}`);
      if (savedOrder) {
        try {
          const parsedOrder = JSON.parse(savedOrder);
          // Merge saved order with default items (in case new items were added)
          const orderedItems = parsedOrder
            .map(path => DEFAULT_NAV_ITEMS.find(item => item.path === path))
            .filter(Boolean);
          // Add any new items that weren't in saved order
          const newItems = DEFAULT_NAV_ITEMS.filter(
            item => !parsedOrder.includes(item.path)
          );
          setNavItems([...orderedItems, ...newItems]);
        } catch (e) {
          setNavItems(DEFAULT_NAV_ITEMS);
        }
      }
    }
  }, [user?.id]);

  // Save nav order to localStorage
  const saveNavOrder = useCallback((items) => {
    if (user?.id) {
      const order = items.map(item => item.path);
      localStorage.setItem(`qindex_nav_order_${user.id}`, JSON.stringify(order));
    }
  }, [user?.id]);

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/login');
    }
  }, [authLoading, token, navigate]);

  useEffect(() => {
    const fetchClient = async () => {
      if (!token || !clientId) return;
      try {
        const response = await api.getClient(token, clientId);
        setClient(response.data);
      } catch (error) {
        console.error('Failed to fetch client:', error);
        navigate('/app');
      }
      setLoading(false);
    };
    fetchClient();
  }, [token, clientId, navigate]);

  // Check if onboarding should continue in client view
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!token) return;
      try {
        const response = await api.getUserPreferences(token);
        if (response.data?.onboarding_completed === false) {
          // Show onboarding in client view
          setTimeout(() => setShowOnboarding(true), 300);
        }
      } catch (error) {
        logger.warn('ClientLayout: onboarding state lookup failed', error);
      }
    };
    checkOnboarding();
  }, [token]);

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    try {
      await api.updateUserPreferences(token, { onboarding_completed: true });
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    setShowOnboarding(false);
    try {
      await api.updateUserPreferences(token, { onboarding_completed: true });
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    if (isLocked) return;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (isLocked || draggedItem === null) return;
    setDragOverItem(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (isLocked || draggedItem === null || draggedItem === dropIndex) return;

    const newItems = [...navItems];
    const draggedItemContent = newItems[draggedItem];
    
    // Remove dragged item
    newItems.splice(draggedItem, 1);
    // Insert at new position
    newItems.splice(dropIndex, 0, draggedItemContent);
    
    setNavItems(newItems);
    saveNavOrder(newItems);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const toggleLock = () => {
    setIsLocked(!isLocked);
  };

  const resetOrder = () => {
    setNavItems(DEFAULT_NAV_ITEMS);
    saveNavOrder(DEFAULT_NAV_ITEMS);
  };

  const currentPath = location.pathname.split('/').pop();
  const isActive = (path) => currentPath === path || 
    (path === 'individuals' && location.pathname.includes('/individuals/')) ||
    (path === 'advisers' && location.pathname.includes('/advisers/')) ||
    (path === 'entities' && location.pathname.includes('/entities/'));

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5D524D]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen overflow-x-hidden" data-testid="client-layout">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-[#E6E0D9]"
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed left-0 top-0 h-screen w-[280px] bg-[#F0EBE6] border-r border-[#D9D6D0] z-50 overflow-y-auto flex flex-col custom-scrollbar",
          "transition-transform duration-300",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
        data-testid="client-sidebar"
      >
        {/* Close button for mobile */}
        <div className="md:hidden p-2 flex justify-end border-b border-[#D9D6D0]">
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 hover:bg-[#F5F3F1] rounded-lg"
          >
            <X className="w-5 h-5 text-[#5D524D]" />
          </button>
        </div>
        
        {/* Sidebar Header */}
        <div className="p-6 border-b border-[#D9D6D0]">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-[#4A3B32] rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">Q1</span>
            </div>
            <span className="font-heading text-lg text-[#2C2420]">QIndex One</span>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/app')}
            className="caffeine-btn-ghost w-full justify-start text-sm"
            data-testid="back-to-family-groups-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Family Groups
          </Button>
        </div>

        {/* Client Info */}
        <div className="p-6 border-b border-[#D9D6D0]">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-heading text-xl text-[#2C2420]">{client?.name}</h2>
            {client?.restricted && (
              <Shield className="w-4 h-4 text-[#7D8C9E]" />
            )}
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            client?.segment === 'Family Office' 
              ? 'bg-[#4A3B32]/10 text-[#4A3B32]' 
              : 'bg-[#C6A88F]/20 text-[#5D524D]'
          }`}>
            {client?.segment}
          </span>
          {client?.restricted && (
            <div className="mt-3 p-2 bg-[#7D8C9E]/10 border border-[#7D8C9E]/20 rounded text-xs text-[#7D8C9E]">
              Restricted access applies
            </div>
          )}
        </div>

        {/* Navigation with Lock/Unlock */}
        <div className="p-4">
          {/* Lock/Unlock Header */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-[#8C8078] font-medium uppercase tracking-wide">Navigation</span>
            <div className="flex items-center gap-1">
              {!isLocked && (
                <button
                  onClick={resetOrder}
                  className="text-xs text-[#8C8078] hover:text-[#4A3B32] px-2 py-1 rounded transition-colors"
                  title="Reset to default order"
                  data-testid="reset-nav-order-btn"
                >
                  Reset
                </button>
              )}
              <button
                onClick={toggleLock}
                className={cn(
                  "p-1.5 rounded-md transition-all",
                  isLocked 
                    ? "text-[#8C8078] hover:text-[#4A3B32] hover:bg-[#4A3B32]/5" 
                    : "text-[#4A3B32] bg-[#4A3B32]/10"
                )}
                title={isLocked ? "Unlock to reorder" : "Lock navigation"}
                data-testid="nav-lock-toggle-btn"
              >
                {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Hint when unlocked */}
          {!isLocked && (
            <div className="mb-3 p-2 bg-[#4A3B32]/5 border border-[#4A3B32]/10 rounded-md">
              <p className="text-xs text-[#5D524D]">
                Drag items to reorder. Click the lock to save.
              </p>
            </div>
          )}

          {/* Navigation Items — grouped + collapsible */}
          <nav data-testid="client-nav">
            {(() => {
              // Compute group visibility from current navItems
              const itemsByGroup = {};
              navItems.forEach((item) => {
                const g = item.group || '_top';
                if (!itemsByGroup[g]) itemsByGroup[g] = [];
                itemsByGroup[g].push(item);
              });

              const renderItem = (item, flatIndex) => {
                const IconComponent = ICON_MAP[item.icon];
                return (
                  <div
                    key={item.path}
                    draggable={!isLocked}
                    onDragStart={(e) => handleDragStart(e, flatIndex)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, flatIndex)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, flatIndex)}
                    className={cn(
                      'relative',
                      dragOverItem === flatIndex && draggedItem !== flatIndex && "before:absolute before:left-0 before:right-0 before:top-0 before:h-0.5 before:bg-[#4A3B32]"
                    )}
                  >
                    <button
                      onClick={() => {
                        if (isLocked) {
                          navigate(`/app/clients/${clientId}/${item.path}`);
                          setMobileMenuOpen(false);
                        }
                      }}
                      className={cn(
                        'sidebar-nav-item w-full text-left mb-1 group',
                        isActive(item.path) && 'active',
                        !isLocked && 'cursor-grab active:cursor-grabbing',
                        draggedItem === flatIndex && 'opacity-50'
                      )}
                      data-testid={`nav-${item.path}`}
                    >
                      {!isLocked && (
                        <GripVertical className="w-3 h-3 text-[#8C8078] opacity-0 group-hover:opacity-100 transition-opacity absolute left-1" />
                      )}
                      <span className={cn("flex items-center gap-2", !isLocked && "ml-4")}>
                        {IconComponent && <IconComponent className="w-4 h-4" />}
                        {item.label}
                      </span>
                    </button>
                  </div>
                );
              };

              // Walk the flat navItems list, but break out group headers as we go.
              const rendered = [];
              const renderedGroups = new Set();
              let topRendered = false;

              navItems.forEach((item, flatIndex) => {
                const g = item.group;
                if (!g) {
                  rendered.push(renderItem(item, flatIndex));
                  topRendered = true;
                  return;
                }
                // First item of a group → render the header.
                if (!renderedGroups.has(g)) {
                  renderedGroups.add(g);
                  const def = GROUPS.find((x) => x.key === g);
                  const label = def?.label || g;
                  const collapsed = !!collapsedGroups[g];
                  rendered.push(
                    <div key={`group-${g}`} className={cn('mt-3 mb-1', topRendered && 'pt-2 border-t border-[#E6E0D9]/60')}>
                      <button
                        onClick={() => toggleGroup(g)}
                        className="w-full flex items-center justify-between px-2 py-1 text-[10px] uppercase tracking-wide text-[#8C8078] hover:text-[#4A3B32] transition-colors"
                        data-testid={`nav-group-toggle-${g}`}
                        aria-expanded={!collapsed}
                      >
                        <span>{label}</span>
                        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    </div>
                  );
                }
                // Only render the item if its group isn't collapsed.
                if (!collapsedGroups[g]) {
                  rendered.push(renderItem(item, flatIndex));
                }
              });

              return rendered;
            })()}
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="mt-auto p-4 border-t border-[#D9D6D0]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-sm font-medium">
              {user?.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#2C2420] truncate">{user?.name}</p>
              <p className="text-xs text-[#8C8078]">{user?.role}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => { logout(); window.location.href = '/login'; }}
            className="caffeine-btn-ghost w-full justify-start text-sm"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen bg-[#F9F7F5] p-4 pt-16 max-w-[100vw] w-full md:ml-[280px] md:p-8 md:pt-8 md:max-w-[calc(100vw-280px)] md:w-auto">
        <div className="relative z-[1] overflow-x-hidden">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-6">
            <div />
            <Button 
              variant="ghost" 
              onClick={() => navigate('/app/search', { state: { from: location.pathname } })}
              className="caffeine-btn-ghost"
              data-testid="client-search-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>

          {/* Page Content */}
          <Outlet context={{ client, clientId }} />
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-[#E6E0D9] text-center">
          <p className="text-xs text-[#8C8078]" data-testid="client-layout-footer">
            Demo as at: {new Date().toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: false })} • Documents stored securely. External sources indexed.
          </p>
        </div>
      </main>

      {/* Onboarding Tour (continues from Dashboard) */}
      <Onboarding
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
    </div>
  );
}
